import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { SortColumn } from "src/app/models/sortColumn.model";
import { FiltersQueryTransformer } from "src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer";
import { IPageFilters } from "src/app/models/filters/interfaces/pageFilters.interface";
import { MultiSelectFilters } from "src/app/models/filters/implementations/filtersTypes/multiFilter.model";
import { FilterTypesEnum } from "src/app/models/filters/filterTypes.enum";
import { ISelectOption, SeveritySelectOptions } from "src/app/models/selectOptions";
import { SectionType } from "../violation-rules.component";
import { OwaspSelectOptionAll, OwaspSelectOptions } from "src/app/models/selectOptions/owaspSelectOptions.model";
import { computed, Signal, signal, WritableSignal } from "@angular/core";
import { OwaspLlmSelectOptionAll, OwaspLlmSelectOptions } from "src/app/models/selectOptions/owaspLlmSelectOptions.model";
import { AttackSelectOptionAll, AttackSelectOptions } from "src/app/models/selectOptions/attackSelectOptions.model";
import { GenAiMatrixSelectOptionsAll, GenAiMatrixSelectOptions } from "src/app/models/selectOptions/genAiMatrixSelectOptions.model";
import { ViolationRulesFilter } from "./violation-rules-vertical-filters.component";
import { ISelectFilter } from "src/app/models/filters/interfaces/selectFilter.interface";
import { ISelectOptionGroups } from "src/app/models/selectOptions/selectOptionGroups.interface";
import { Label } from "src/app/labels/englishLabels";
import { RuleTagSelectOptions } from "src/app/models/selectOptions/ruleTagsSelectOptions.model";
import { ViolationRuleStatusSelectOptions } from "src/app/models/selectOptions/violationRuleStatusSelectOptions.model";

export type ViolationRulesFiltersValues = { group: string[]; tags: string[]; status: string[]; severity: string[] };
export type OptionGroup = { filter: ViolationRulesFilter; group: ISelectOptionGroups };
type Filters = Record<ViolationRulesFilter, { type: ViolationRulesFilter; instance: ISelectFilter }>;
type CommonFilters = Record<
    Exclude<ViolationRulesFilter, ViolationRulesFilter.GROUP_NAME>,
    { type: Exclude<ViolationRulesFilter, ViolationRulesFilter.GROUP_NAME>; instance: ISelectFilter }
> | null;

export class ViolationRulesFiltersModel implements IPageFilters<ViolationRulesFiltersValues, ViolationRulesFilter> {
    public onUpdate$: BehaviorSubject<ViolationRulesFiltersValues>;
    public allOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

    public filters: WritableSignal<Filters | null> = signal(null);
    public optionsGroups: Signal<OptionGroup[]>;

    public sortedColumn$: BehaviorSubject<SortColumn | undefined> = new BehaviorSubject<SortColumn | undefined>(undefined);

    private _activeSection: SectionType | null = null;
    private _sectionSpecificOptions: Record<SectionType, { allOptionId: string; options: ISelectFilter } | null> = {
        [SectionType.OWASP]: { allOptionId: OwaspSelectOptionAll.id, options: new MultiSelectFilters([OwaspSelectOptionAll, ...OwaspSelectOptions]) },
        [SectionType.OWASP_LLM]: {
            allOptionId: OwaspLlmSelectOptionAll.id,
            options: new MultiSelectFilters([OwaspLlmSelectOptionAll, ...OwaspLlmSelectOptions]),
        },
        [SectionType.MITRE]: {
            allOptionId: AttackSelectOptionAll.id,
            options: new MultiSelectFilters([AttackSelectOptionAll, ...AttackSelectOptions]),
        },
        [SectionType.GEN_AI_MATRIX]: {
            allOptionId: GenAiMatrixSelectOptionsAll.id,
            options: new MultiSelectFilters([GenAiMatrixSelectOptionsAll, ...GenAiMatrixSelectOptions]),
        },
        [SectionType.NONE]: null,
    };
    private _commonOptions: CommonFilters = {
        [ViolationRulesFilter.TAGS]: { type: ViolationRulesFilter.TAGS, instance: new MultiSelectFilters([...RuleTagSelectOptions]) },
        [ViolationRulesFilter.STATUS]: { type: ViolationRulesFilter.STATUS, instance: new MultiSelectFilters([...ViolationRuleStatusSelectOptions]) },
        [ViolationRulesFilter.SEVERITY]: { type: ViolationRulesFilter.SEVERITY, instance: new MultiSelectFilters([...SeveritySelectOptions]) },
    };

    private _filterNames: Record<string, string> = {
        [ViolationRulesFilter.GROUP_NAME]: Label.get("policyPage.policy.violationRules.groupName"),
        [ViolationRulesFilter.TAGS]: Label.get("policyPage.policy.violationRules.tags"),
        [ViolationRulesFilter.STATUS]: Label.get("policyPage.policy.violationRules.status"),
        [ViolationRulesFilter.SEVERITY]: Label.get("policyPage.policy.violationRules.severity"),
    };

    constructor(private _router: Router) {
        this.onUpdate$ = new BehaviorSubject(this.getSelectedFilters());

        this.optionsGroups = computed(() => {
            const filters = this.filters();
            if (!filters) {
                return [];
            }

            return Object.values(filters).reduce((prev, filter) => {
                const group: ISelectOptionGroups = {
                    selectedCount: 0,
                    displayName: this._filterNames[filter.type],
                    options: filter.instance.getOptions$().value,
                    isDynamic: false,
                    isNew: false,
                };
                prev.push({ filter: filter.type, group } as OptionGroup);
                return prev;
            }, [] as OptionGroup[]);
        });
    }

    public sectionChange(activeSection: SectionType): void {
        this._activeSection = activeSection;
        this._initializeFilters(activeSection);
        this.onUpdate$.next(this.getSelectedFilters());
    }

    public getAllFiltersOptions(): BehaviorSubject<ISelectOption[]> {
        return this.allOptions$;
    }

    public getAllSelectedOptions(): ISelectOption[] {
        const filters = this.filters();
        if (!filters) {
            return [];
        }
        const selectedOptions: ISelectOption[] = [];
        Object.values(filters).forEach((filter) => {
            const selected = filter.instance.getSelectedOptions();
            if (!selected) {
                return;
            }
            if (selected instanceof Array) {
                selectedOptions.push(...selected);
            } else {
                selectedOptions.push(selected);
            }
        });
        return selectedOptions;
    }

    public getSelectedFilters(): ViolationRulesFiltersValues {
        const filters = this.filters();
        if (!filters) {
            return {} as ViolationRulesFiltersValues;
        }
        return {
            group: (filters[ViolationRulesFilter.GROUP_NAME]?.instance.getSelectedFiltersValues() as string[]) ?? [],
            tags: (filters[ViolationRulesFilter.TAGS]?.instance.getSelectedFiltersValues() as string[]) ?? [],
            status: (filters[ViolationRulesFilter.STATUS]?.instance.getSelectedFiltersValues() as string[]) ?? [],
            severity: (filters[ViolationRulesFilter.SEVERITY]?.instance.getSelectedFiltersValues() as string[]) ?? [],
        };
    }

    public getDynamicFilters(): string[] {
        return [];
    }

    public getNewFilters(): string[] {
        return [];
    }

    public updateFilterFromUserSelection(
        filterToUpdate: ViolationRulesFilter | undefined,
        value: ISelectOption[] | Date | boolean | string | undefined,
    ) {
        const activeSection = this._activeSection;
        const filters = this.filters();

        if (activeSection === null || !filters) {
            return;
        }

        let needUpdate = false;

        switch (filterToUpdate) {
            case ViolationRulesFilter.GROUP_NAME:
                needUpdate = this._changeGroupName(
                    value as ISelectOption[],
                    activeSection,
                    filters[ViolationRulesFilter.GROUP_NAME].instance as MultiSelectFilters,
                );
                break;
            case ViolationRulesFilter.TAGS:
            case ViolationRulesFilter.STATUS:
            case ViolationRulesFilter.SEVERITY:
                this._updateAllOptions();
                filters[filterToUpdate].instance.updateIfNeeded(value as ISelectOption[]);
                break;

            default:
                console.warn("Unhandled filter");
        }

        if (needUpdate) {
            this._handleSortedColumnOnFilter();
            const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
            this._router.navigate([], {
                queryParams: { q: filtersQuery },
                queryParamsHandling: "merge",
            });
            this.onUpdate$.next(this.getSelectedFilters());
        }
    }

    public removeSelectedOptionsIfExist(options: ISelectOption[]): void {
        const optionsIdsByCategory: { [key: string]: Set<string> } = {};
        options.forEach((option) => {
            if (option.filterType) {
                if (option.filterType in optionsIdsByCategory) {
                    optionsIdsByCategory[option.filterType].add(option.id);
                } else {
                    optionsIdsByCategory[option.filterType] = new Set([option.id]);
                }
            }
        });

        const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
        this._router.navigate([], {
            queryParams: { q: filtersQuery },
            queryParamsHandling: "merge",
        });
        this.onUpdate$.next(this.getSelectedFilters());
    }

    public getFilterOptionsIfExist$(filter: FilterTypesEnum): BehaviorSubject<ISelectOption[]> {
        switch (filter) {
            default:
                throw new Error(`ViolationsFiltersModel getFilterOptionsIfExist: ${filter} not supported`);
        }
    }

    public getFilterSelectedIfExist(filter: FilterTypesEnum): string[] {
        switch (filter) {
            default:
                throw new Error(`ViolationsFiltersModel getFilterSelectedIfExist: ${filter} not supported`);
        }
    }

    public updateFiltersByKeyValue(keyValueFilters: { [key: string]: string[] }, updateQueryParams = false): void {
        let needUpdate = false;

        if (needUpdate) {
            if (updateQueryParams) {
                const filtersQuery = FiltersQueryTransformer.transformToQuery(this.getSelectedFilters());
                this._router.navigate([], {
                    queryParams: { q: filtersQuery },
                    queryParamsHandling: "merge",
                });
            }
            this.onUpdate$.next(this.getSelectedFilters());
        }
    }

    public restoreSortColumnToDefaultIfNeeded() {}

    private _initializeFilters(activeSection: SectionType) {
        let filters: Filters = {} as Filters;
        const sectionSpecific = this._sectionSpecificOptions[activeSection];
        if (sectionSpecific) {
            filters[ViolationRulesFilter.GROUP_NAME] = { type: ViolationRulesFilter.GROUP_NAME, instance: sectionSpecific.options };
        }
        filters = {
            ...filters,
            ...this._commonOptions,
        };

        this.filters.set(filters);

        this._updateAllOptions();
    }

    private _updateAllOptions() {
        const filters = this.filters();
        if (filters) {
            this.allOptions$.next(Object.values(filters).flatMap((filter) => filter.instance.getOptions$().value ?? []));
        }
    }

    private _handleSortedColumnOnFilter() {
        if (this.sortedColumn$.value === undefined) {
            this.restoreSortColumnToDefaultIfNeeded();
        }
    }

    private _changeGroupName(value: ISelectOption[], activeSection: SectionType, filters: MultiSelectFilters) {
        const includesAllOption = value.find((option) => option.id === this._sectionSpecificOptions[activeSection]?.allOptionId);
        const previousValue = filters.getSelectedOptions();
        const previousValueIncludesAllOption = previousValue.find((option) => option.id === this._sectionSpecificOptions[activeSection]?.allOptionId);
        const allPreviousOptions = filters.getOptions$().value;

        if (includesAllOption && !previousValueIncludesAllOption) {
            value = filters.getOptions$().value;
        }
        if (previousValueIncludesAllOption && !includesAllOption) {
            value = [];
        }
        if (includesAllOption && value.length !== allPreviousOptions.length) {
            value = value.filter((option) => option.id !== this._sectionSpecificOptions[activeSection]?.allOptionId);
        }

        if (value.length == 0) {
            return filters.updateIfNeeded([]);
        } else {
            this._updateAllOptions();
            return filters.updateIfNeeded(value);
        }
    }
}
